import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import {
  Buildings,
  Users,
  UserCheck,
  Calendar,
  Envelope,
  CreditCard,
  Trash,
  UserPlus,
  Pencil,
  MagnifyingGlass,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { COMPANY_STATUS_CONFIG } from "@/lib/status-configs";
import type { Company, Supervisor } from "@/types";
import { formatDate } from "./helpers";
import { UserSearchPicker } from "./UserSearchPicker";
import { EmailInvitePicker } from "./EmailInvitePicker";

const STATUS_CONFIG = COMPANY_STATUS_CONFIG;

export interface CompanyDetailDialogProps {
  company: Company;
  onEdit: () => void;
  onClose: () => void;
}

function InfoItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </div>
      <p className="mt-0.5 truncate text-sm font-medium text-foreground">
        {value}
      </p>
    </div>
  );
}

export function CompanyDetailDialog({
  company,
  onEdit,
  onClose,
}: CompanyDetailDialogProps) {
  const queryClient = useQueryClient();
  const config = STATUS_CONFIG[company.status];
  const [addMode, setAddMode] = useState<null | "search" | "invite">(null);

  const { data: supervisors = [] } = useQuery({
    queryKey: ["admin", "companies", company.id, "supervisors"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${company.id}/supervisors`);
      if (!res.ok) throw new Error("Failed to fetch supervisors");
      return res.json() as Promise<Supervisor[]>;
    },
  });

  function onSupervisorAdded() {
    queryClient.invalidateQueries({
      queryKey: ["admin", "companies", company.id, "supervisors"],
    });
    queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
    setAddMode(null);
  }

  const removeSupervisorMutation = useMutation({
    mutationFn: async (supervisorId: string) => {
      const res = await fetch(
        `/api/companies/${company.id}/supervisors/${supervisorId}`,
        { method: "DELETE" }
      );
      await throwIfNotOk(res, "Greška pri uklanjanju supervizora");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "companies", company.id, "supervisors"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
    },
  });

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/8">
            <Buildings className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="truncate">{company.name}</DialogTitle>
            <span
              className={cn(
                "mt-0.5 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1",
                config.bg,
                config.color,
                config.ring
              )}
            >
              <span className={cn("size-1.5 rounded-full", config.dot)} />
              {config.label.slice(0, -1)}
            </span>
          </div>
        </div>
      </DialogHeader>

      <DialogBody>
        <div className="flex flex-col gap-6">
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-4">
            <InfoItem
              icon={Envelope}
              label="Billing email"
              value={company.billingEmail}
            />
            <InfoItem
              icon={CreditCard}
              label="Plan"
              value={company.planType}
            />
            <InfoItem
              icon={Users}
              label="Zaposleni"
              value={`${company.employeeCount} / ${company.maxEmployees}`}
            />
            <InfoItem
              icon={UserCheck}
              label="Supervizori"
              value={String(company.supervisorCount)}
            />
            <InfoItem
              icon={Calendar}
              label="Kreirano"
              value={formatDate(company.createdAt)}
            />
          </div>

          {company.notes && (
            <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CreditCard className="size-3.5" />
                Ugovor / napomene
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                {company.notes}
              </p>
            </div>
          )}

          {/* Supervisors section */}
          <div>
            <div className="flex items-center justify-between">
              <h3
                className="text-sm font-semibold text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Supervizori
              </h3>
              {!addMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddMode("search")}
                >
                  <UserPlus className="size-3.5" />
                  Dodaj
                </Button>
              )}
            </div>

            {addMode && (
              <div className="mt-3 space-y-3">
                {/* Toggle tabs */}
                <div className="flex gap-1.5 rounded-lg bg-muted/60 p-1">
                  <button
                    onClick={() => setAddMode("search")}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                      addMode === "search"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <MagnifyingGlass className="size-3" />
                    Pronađi korisnika
                  </button>
                  <button
                    onClick={() => setAddMode("invite")}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                      addMode === "invite"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <PaperPlaneTilt className="size-3" />
                    Pozovi putem emaila
                  </button>
                </div>

                {addMode === "search" ? (
                  <UserSearchPicker
                    companyId={company.id}
                    onAdded={onSupervisorAdded}
                    onCancel={() => setAddMode(null)}
                  />
                ) : (
                  <EmailInvitePicker
                    companyId={company.id}
                    onAdded={onSupervisorAdded}
                    onCancel={() => setAddMode(null)}
                  />
                )}
              </div>
            )}

            <div className="mt-3">
              {supervisors.length === 0 && !addMode ? (
                <p className="rounded-xl border border-dashed border-border/60 bg-muted/20 py-6 text-center text-sm text-muted-foreground">
                  Nema dodanih supervizora.
                </p>
              ) : supervisors.length > 0 ? (
                <div className="space-y-2">
                  {supervisors.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5"
                    >
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {s.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {s.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {s.email}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                        disabled={removeSupervisorMutation.isPending}
                        onClick={() => {
                          if (confirm(`Ukloniti ${s.name} kao supervizora?`)) {
                            removeSupervisorMutation.mutate(s.id);
                          }
                        }}
                      >
                        <Trash className="size-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
        <Button onClick={onEdit}>
          <Pencil className="size-4" />
          Uredi firmu
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
